import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Grid,
  Typography,
  Stack,
  RatingProps,
  Rating,
  Chip,
  SxProps,
  RadioGroup,
  FormControlLabel,
  Radio,
  Collapse,
} from '@mui/material';
import moment from 'moment';

import {
  CustomCardContent,
  defaultRatingStyles,
  EmptyRateIcon,
  IFeedbackTutor,
  RateIcon,
} from './helper';
import {
  createLessonStudentFeedback,
  getLessonStudentFeedbackRewards,
  getLessonStudentFeedbacksState,
  ILessonStudentFeedback,
  resetLessonStudentFeedbackRewardsData,
  TCreateLessonStudentFeedback,
  TLessonStudentFeedbackRewardItem,
  TUpdateLessonStudentFeedback,
  updateLessonStudentFeedbacks,
} from '../../../store/V2/LessonStudentFeedbacksSlice';
import { useAppSelector } from '../../../hooks';
import { TPTextField } from '../../Inputs';
import { IUser } from '../../../store/UserSlice';
import { getShortName } from '../../../helper';

interface AddStudentFeedbackFormProps {
  sectionId: string;
  tutors: IFeedbackTutor[];
  feedback?: ILessonStudentFeedback;
  onFeedbackAdded?: (feedbackData: ILessonStudentFeedback[]) => void;
  onFeedbackUpdated?: (
    id: string,
    feedbackData: ILessonStudentFeedback
  ) => void;
}

type TState = {
  overallTutorRating: number | null;
  feedbackRewards: string[];
  teachingConcept: number | null;
  questionPractice: number | null;
  communicationSkills: number | null;
  isContinueLesson: 'Yes' | 'No';
  subjectAndSyllabusKnowledge: number | null;
  punctualAndProfessional: number | null;
  technicalSkills: number | null;
  overallTutopiyaRating: number | null;
  feedbackComments: string;
};

const actionButtonStyles: SxProps = {
  padding: { xs: '6px 12px', sm: '12px 24px' },
  borderRadius: '6px',
  fontWeight: 600,
  fontSize: '12px',
};

export const overallOutOfRating = 5;
const commonRatingProps: RatingProps = {
  icon: RateIcon,
  emptyIcon: EmptyRateIcon,
};

const initialState: TState = {
  overallTutorRating: null,
  feedbackRewards: [],
  teachingConcept: null,
  questionPractice: null,
  communicationSkills: null,
  isContinueLesson: 'Yes',
  subjectAndSyllabusKnowledge: null,
  punctualAndProfessional: null,
  technicalSkills: null,
  overallTutopiyaRating: null,
  feedbackComments: '',
};

const AddStudentFeedbackForm: FunctionComponent<AddStudentFeedbackFormProps> = ({
  sectionId,
  feedback,
  onFeedbackAdded,
  onFeedbackUpdated,
  tutors,
}) => {
  const dispatch = useDispatch();

  // Store
  const lessonStudentFeedbacksState = useAppSelector(
    getLessonStudentFeedbacksState
  );

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const mode: 'new' | 'edit' = feedback ? 'edit' : 'new';
  const isSubmitDisabled =
    !state.overallTutorRating ||
    !state.teachingConcept ||
    !state.questionPractice ||
    !state.communicationSkills ||
    !state.subjectAndSyllabusKnowledge ||
    !state.punctualAndProfessional ||
    !state.technicalSkills;

  let selectedFeedbackRewardData: TLessonStudentFeedbackRewardItem | undefined;
  let lastUpdatedAt = '';
  let createdAt = '';
  let feedbackTutor: IUser | undefined;

  if (
    lessonStudentFeedbacksState.lessonStudentFeedbackRewardsData &&
    lessonStudentFeedbacksState.lessonStudentFeedbackRewardsData[
      state.overallTutorRating || 0
    ]
  ) {
    selectedFeedbackRewardData =
      lessonStudentFeedbacksState.lessonStudentFeedbackRewardsData[
        state.overallTutorRating || 0
      ];
  }

  if (feedback) {
    feedbackTutor = feedback.tutor as IUser;

    if (feedback.createdAt) {
      createdAt = moment(feedback.createdAt).format('ddd MMM DD YYYY, hh:mm A');
    }

    if (feedback.updatedAt) {
      lastUpdatedAt = moment(feedback.updatedAt).format(
        'ddd MMM DD YYYY, hh:mm A'
      );
    }
  }

  useEffect(() => {
    dispatch(getLessonStudentFeedbackRewards());

    if (mode === 'edit' && feedback) {
      setState((prevState) => {
        const newStateData = { ...prevState };

        if (feedback.overallTutorRating) {
          newStateData.overallTutorRating = feedback.overallTutorRating || null;
        }

        if (feedback.feedbackRewards && feedback.feedbackRewards.length) {
          newStateData.feedbackRewards = feedback.feedbackRewards;
        }

        if (feedback.teachingConcept) {
          newStateData.teachingConcept = feedback.teachingConcept;
        }

        if (feedback.questionPractice) {
          newStateData.questionPractice = feedback.questionPractice;
        }

        if (feedback.communicationSkills) {
          newStateData.communicationSkills = feedback.communicationSkills;
        }

        if (feedback.isContinueLesson) {
          newStateData.isContinueLesson = feedback.isContinueLesson;
        }

        if (feedback.subjectAndSyllabusKnowledge) {
          newStateData.subjectAndSyllabusKnowledge =
            feedback.subjectAndSyllabusKnowledge;
        }

        if (feedback.punctualAndProfessional) {
          newStateData.punctualAndProfessional =
            feedback.punctualAndProfessional;
        }

        if (feedback.technicalSkills) {
          newStateData.technicalSkills = feedback.technicalSkills;
        }

        if (feedback.overallTutopiyaRating) {
          newStateData.overallTutopiyaRating = feedback.overallTutopiyaRating;
        }

        if (feedback.feedbackComments) {
          newStateData.feedbackComments = feedback.feedbackComments;
        }

        return newStateData;
      });
    }

    return () => {
      dispatch(resetLessonStudentFeedbackRewardsData());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const onFeedbackRewardOptionChange = (option: string) => {
    setState((prevState) => {
      let newList = [...prevState.feedbackRewards];

      if (newList.includes(option)) {
        newList = newList.filter((item) => item !== option);
      } else {
        newList.push(option);
      }

      return {
        ...prevState,
        feedbackRewards: newList,
      };
    });
  };

  const handleSubmit = async () => {
    if (
      state.overallTutorRating &&
      state.teachingConcept &&
      state.questionPractice &&
      state.communicationSkills &&
      state.subjectAndSyllabusKnowledge &&
      state.punctualAndProfessional &&
      state.technicalSkills
    ) {
      if (mode === 'new' && tutors.length) {
        setLoading(true);

        const apiData: TCreateLessonStudentFeedback = {
          tutors: tutors.map((item) => item._id),
          section: sectionId,
          feedbackRewards: state.feedbackRewards,
          overallTutorRating: state.overallTutorRating,
          teachingConcept: state.teachingConcept,
          questionPractice: state.questionPractice,
          communicationSkills: state.communicationSkills,
          isContinueLesson: state.isContinueLesson,
          subjectAndSyllabusKnowledge: state.subjectAndSyllabusKnowledge,
          punctualAndProfessional: state.punctualAndProfessional,
          technicalSkills: state.technicalSkills,
        };

        if (state.overallTutopiyaRating) {
          apiData.overallTutopiyaRating = state.overallTutopiyaRating;
        }

        if (state.feedbackComments) {
          apiData.feedbackComments = state.feedbackComments;
        }

        const res: any = await dispatch(createLessonStudentFeedback(apiData));

        if (res.payload && onFeedbackAdded) {
          onFeedbackAdded(res.payload);
        }

        setLoading(false);
      } else if (mode === 'edit' && feedback) {
        setLoading(true);

        const apiData: TUpdateLessonStudentFeedback = {
          id: feedback._id,
          feedbackRewards: state.feedbackRewards,
          overallTutorRating: state.overallTutorRating,
          teachingConcept: state.teachingConcept,
          questionPractice: state.questionPractice,
          communicationSkills: state.communicationSkills,
          isContinueLesson: state.isContinueLesson,
          subjectAndSyllabusKnowledge: state.subjectAndSyllabusKnowledge,
          punctualAndProfessional: state.punctualAndProfessional,
          technicalSkills: state.technicalSkills,
        };

        if (state.overallTutopiyaRating) {
          apiData.overallTutopiyaRating = state.overallTutopiyaRating;
        }

        if (state.feedbackComments) {
          apiData.feedbackComments = state.feedbackComments;
        }

        const res: any = await dispatch(updateLessonStudentFeedbacks(apiData));

        if (res.payload && onFeedbackUpdated) {
          onFeedbackUpdated(feedback._id, res.payload);
        }

        setLoading(false);
      }
    }
  };

  return (
    <div>
      <Grid container rowSpacing="10px">
        <Grid item xs={12}>
          <CustomCardContent>
            <Typography fontSize="16px" fontWeight={600} textAlign="center">
              Overall rating
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="center">
              <Typography fontWeight={900} fontSize="30px">
                <span style={{ fontSize: '50px' }}>
                  {state.overallTutorRating || 0}
                </span>
                /{overallOutOfRating}
              </Typography>
              <Rating
                {...commonRatingProps}
                value={state.overallTutorRating}
                sx={{ ml: '10px', ...defaultRatingStyles }}
                onChange={(e, newValue) => {
                  updateStateAttributes({
                    overallTutorRating: newValue,
                    feedbackRewards: [],
                  });
                }}
              />
            </Stack>
            <Collapse
              in={Boolean(
                selectedFeedbackRewardData &&
                  selectedFeedbackRewardData.list &&
                  selectedFeedbackRewardData.list.length
              )}
            >
              {selectedFeedbackRewardData &&
              selectedFeedbackRewardData.list &&
              selectedFeedbackRewardData.list.length ? (
                <Stack>
                  <Typography
                    fontSize="16px"
                    fontWeight={600}
                    textAlign="center"
                    mb="7px"
                  >
                    {selectedFeedbackRewardData.title || ''}
                  </Typography>
                  <Stack
                    gap="5px"
                    direction="row"
                    flexWrap="wrap"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {selectedFeedbackRewardData.list.map((item) => {
                      const isSelected = state.feedbackRewards.includes(item);
                      const chipStyles: SxProps = {
                        fontWeight: 500,
                        fontSize: '12px',
                        borderRadius: '20px',
                      };

                      if (isSelected) {
                        chipStyles.backgroundColor = '#01A79F';
                        chipStyles.color = '#F8FAFC';
                        chipStyles['&:hover'] = {
                          backgroundColor: '#01A79F',
                          color: '#F8FAFC',
                        };
                      }

                      return (
                        <Chip
                          key={item}
                          label={item}
                          sx={chipStyles}
                          onClick={() => onFeedbackRewardOptionChange(item)}
                        />
                      );
                    })}
                  </Stack>
                </Stack>
              ) : null}
            </Collapse>
          </CustomCardContent>
        </Grid>
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center">
              <Typography fontSize="16px" fontWeight={600} mb="7px">
                Teaching concepts *
              </Typography>
              <Rating
                {...commonRatingProps}
                value={state.teachingConcept}
                sx={{ ...defaultRatingStyles }}
                onChange={(e, newValue) => {
                  updateStateAttributes({ teachingConcept: newValue });
                }}
              />
            </Stack>
          </CustomCardContent>
        </Grid>
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center">
              <Typography fontSize="16px" fontWeight={600} mb="7px">
                Question practice *
              </Typography>
              <Rating
                {...commonRatingProps}
                value={state.questionPractice}
                sx={{ ...defaultRatingStyles }}
                onChange={(e, newValue) => {
                  updateStateAttributes({ questionPractice: newValue });
                }}
              />
            </Stack>
          </CustomCardContent>
        </Grid>
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center">
              <Typography fontSize="16px" fontWeight={600} mb="7px">
                Communication skills *
              </Typography>
              <Rating
                {...commonRatingProps}
                value={state.communicationSkills}
                sx={{ ...defaultRatingStyles }}
                onChange={(e, newValue) => {
                  updateStateAttributes({ communicationSkills: newValue });
                }}
              />
            </Stack>
          </CustomCardContent>
        </Grid>
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center">
              <Typography fontSize="16px" fontWeight={600} mb="7px">
                Do you like to continue lessons? *
              </Typography>
              <RadioGroup
                row
                value={state.isContinueLesson}
                onChange={(e) =>
                  updateStateAttributes({
                    isContinueLesson: e.target
                      .value as TState['isContinueLesson'],
                  })
                }
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Stack>
          </CustomCardContent>
        </Grid>
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center">
              <Typography fontSize="16px" fontWeight={600} mb="7px">
                Subject and syllabus knowledge *
              </Typography>
              <Rating
                {...commonRatingProps}
                value={state.subjectAndSyllabusKnowledge}
                sx={{ ...defaultRatingStyles }}
                onChange={(e, newValue) => {
                  updateStateAttributes({
                    subjectAndSyllabusKnowledge: newValue,
                  });
                }}
              />
            </Stack>
          </CustomCardContent>
        </Grid>
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center">
              <Typography fontSize="16px" fontWeight={600} mb="7px">
                Punctual & professional *
              </Typography>
              <Rating
                {...commonRatingProps}
                value={state.punctualAndProfessional}
                sx={{ ...defaultRatingStyles }}
                onChange={(e, newValue) => {
                  updateStateAttributes({
                    punctualAndProfessional: newValue,
                  });
                }}
              />
            </Stack>
          </CustomCardContent>
        </Grid>
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center">
              <Typography fontSize="16px" fontWeight={600} mb="7px">
                Technical skills *
              </Typography>
              <Rating
                {...commonRatingProps}
                value={state.technicalSkills}
                sx={{ ...defaultRatingStyles }}
                onChange={(e, newValue) => {
                  updateStateAttributes({
                    technicalSkills: newValue,
                  });
                }}
              />
            </Stack>
          </CustomCardContent>
        </Grid>
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center">
              <Typography fontSize="16px" fontWeight={600} mb="7px">
                Overall rating for Tutopiya&apos;s support
              </Typography>
              <img
                src={`${process.env.PUBLIC_URL}/images/logos/tp-logo-box-256x256.png`}
                alt="support-logo"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '100%',
                  marginBottom: '7px',
                }}
              />
              <Rating
                {...commonRatingProps}
                value={state.overallTutopiyaRating}
                sx={{ ...defaultRatingStyles }}
                onChange={(e, newValue) => {
                  updateStateAttributes({
                    overallTutopiyaRating: newValue,
                  });
                }}
              />
            </Stack>
          </CustomCardContent>
        </Grid>
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center">
              <Typography fontSize="16px" fontWeight={600} mb="7px">
                Comments
              </Typography>
              <TPTextField
                fullWidth
                multiline
                rows={3}
                value={state.feedbackComments}
                placeholder="Share details with our team"
                InputProps={{
                  sx: { backgroundColor: '#ffffff' },
                }}
                onChange={(e) =>
                  updateStateAttributes({
                    feedbackComments: e.target.value,
                  })
                }
              />
            </Stack>
          </CustomCardContent>
        </Grid>
        {feedback ? (
          <Grid item xs={12}>
            <CustomCardContent>
              <Stack alignItems="center" gap="10px">
                {createdAt ? (
                  <Typography fontWeight={500} fontSize="14px" color="#647488">
                    Created at: {createdAt}
                  </Typography>
                ) : null}
                {lastUpdatedAt ? (
                  <Typography fontWeight={500} fontSize="14px" color="#647488">
                    Last updated at: {lastUpdatedAt}
                  </Typography>
                ) : null}
                {feedbackTutor ? (
                  <Chip
                    key={feedbackTutor._id}
                    label={getShortName(
                      feedbackTutor.firstname,
                      feedbackTutor.lastname
                    )}
                  />
                ) : null}
              </Stack>
            </CustomCardContent>
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end">
            <LoadingButton
              disableElevation
              variant="contained"
              color="primary"
              disabled={isSubmitDisabled}
              loading={isLoading}
              sx={{ ...actionButtonStyles }}
              onClick={handleSubmit}
            >
              {mode === 'edit' ? 'Save Feedback' : 'Add Feedback'}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </div>
  );
};

export default AddStudentFeedbackForm;
